import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { DIRECTION_OPTIONS, TIME_FRAME, TRAFFIC_VOLUME_TYPE } from 'utils/constanst';
import { getLabelByScenario } from 'pages/SensorList/helper';
import { downloadFileFromS3 } from 'utils/function';

const downloadExcelFileFromS3 = async (type, unit) => {
  // Number of retry attempts
  const maxRetries = 3;

  const templateKey = `excel_template/${type}/${unit.toLowerCase()}.xlsx`;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // // Download Excel template from S3
      const arrayBuffer = await downloadFileFromS3(templateKey, 'arrayBuffer')
      return arrayBuffer;
    } catch (error) {
      if (attempt === maxRetries) {
        throw new Error(`Attempt ${attempt}/${maxRetries} failed: ${error.message}`);
      };
    }
  }
}

const insertImageToWorksheet = (workbook, worksheet, imgData, type, unit) => {
  const POSITIONS = {
    vehicle: { 
      [TIME_FRAME.TEN_MINUTES]: { nativeCol: 9, nativeColOff: 105750 },
      [TIME_FRAME.DAY]: { nativeCol: 11, nativeColOff: 105750 },
      [TIME_FRAME.WEEK]: { nativeCol: 9, nativeColOff: 105750 },
      [TIME_FRAME.MONTH]: { nativeCol: 9, nativeColOff: 105750 },
      [TIME_FRAME.YEAR]: { nativeCol: 9, nativeColOff: 105750 },
     },
    pedestrian: {
      [TIME_FRAME.TEN_MINUTES]: { nativeCol: 7, nativeColOff: 405750 },
      [TIME_FRAME.DAY]: { nativeCol: 10, nativeColOff: 105750 },
      [TIME_FRAME.WEEK]: { nativeCol: 7, nativeColOff: 405750 },
      [TIME_FRAME.MONTH]: { nativeCol: 7, nativeColOff: 405750 },
      [TIME_FRAME.YEAR]: { nativeCol: 7, nativeColOff: 405750 },
     }
  }

  // Skip if no image data provided
  if (!imgData) {
    console.log('No image data provided, skipping image insertion');
    return;
  }

  try {
    // Validate base64 data
    if (typeof imgData !== 'string' || !imgData.includes('data:image/')) {
      console.warn('Invalid image data format');
      return;
    }

    // Extract base64 data (remove data:image/png;base64, prefix)
    const base64Data = imgData.split(',')[1];
    if (!base64Data || base64Data.length === 0) {
      console.warn('No base64 data found');
      return;
    }

    // Validate base64 string
    try {
      atob(base64Data); // Test if it's valid base64
    } catch (e) {
      console.warn('Invalid base64 encoding');
      return;
    }

    // Add image to workbook with proper configuration
    const imageId = workbook.addImage({
      base64: base64Data,
      extension: 'png',
    });
    console.log(`Image added to workbook with ID for type: ${type}, unit: ${unit}`);
    const nativeCol = POSITIONS[type][unit].nativeCol;
    const nativeColOff = POSITIONS[type][unit].nativeColOff || 0;

    // Insert image into worksheet at column, row 1 and shift bottom -50% with specified size
    // Image size: 450px width, 247px height
    worksheet.addImage(imageId, {
        tl: { nativeCol, nativeColOff , nativeRow: 1, nativeRowOff: -95250 },
        ext: { width: 450, height: 247 }
      });

  } catch (error) {
    console.error('Failed to insert image:', error);
    console.warn('Continuing without image...');
  }
};

const retrieveVehicleCols = (type, index) => {
  if (type === 'date') {
    if (index % 2 === 0)
      return {
        scenarioCol: 'C',
        vehicleCols: ['C', 'D', 'E', 'F', 'G']
      };
    return {
      scenarioCol: 'K',
      vehicleCols: ['K', 'L', 'M', 'N', 'O']
    };
  } else {
    if (index % 2 === 0)
      return {
        scenarioCol: 'C',
        vehicleCols: ['C', 'D', 'E', 'F', 'G']
      };
    return {
      scenarioCol: 'J',
      vehicleCols: ['J', 'K', 'L', 'M', 'N']
    };
  }
}

const fillVehicleData = (worksheet, datasets, timestep, offset) => {
  // Fill vehicle data in columns C-H for even index datasets and J-O for odd index datasets
  for (let index = 0; index < datasets.length; index++) {
    const dataset = datasets[index];

    // Determine vehicleCols based on dataset index (even or odd)
    const { scenarioCol, vehicleCols } = retrieveVehicleCols(offset === 31 ? 'date' : 'other', index);

    // Fill scenario name
    const newIndex = Math.floor(index / 2);
    if (newIndex % 2 === 0) {
      worksheet.getCell(`${scenarioCol}${10 + (offset * newIndex)}`).value = dataset.name;
    } else {
      worksheet.getCell(`${scenarioCol}${10 + (offset * newIndex)}`).value = dataset.name;
    }

    // Fill step data
    for (let step = 0; step < timestep; step++) {
      const stepData = dataset.data[step];
      if (!stepData) continue; // Skip if no data for this step
      worksheet.getCell(`${vehicleCols[0]}${14 + (offset * newIndex) + step}`).value = stepData.car;
      worksheet.getCell(`${vehicleCols[1]}${14 + (offset * newIndex) + step}`).value = stepData.bus;
      worksheet.getCell(`${vehicleCols[2]}${14 + (offset * newIndex) + step}`).value = stepData.bike;
      worksheet.getCell(`${vehicleCols[3]}${14 + (offset * newIndex) + step}`).value = stepData.truck;
      worksheet.getCell(`${vehicleCols[4]}${14 + (offset * newIndex) + step}`).value = stepData.otherVehicle;
    }
  }
}

const fillVehicleTenMinutes = (worksheet, datasets, times) => {
  const timestep = 6;
  const table = 5;
  const offset = 12;

  // Fill time labels in column B
  for (let i = 0; i < table; i++) {
    for (let j = 0; j < timestep; j++) {
      worksheet.getCell(`B${14 + (offset * i) + j}`).value = times[j] || '';
    }
  }

  fillVehicleData(worksheet, datasets, timestep, offset);
}

const fillVehicleWeek = (worksheet, datasets, times) => {
  const table = 5;
  const timestep = 7; // Max days in a week
  const offset = 13;

  // Fill time labels in column B
  for (let i = 0; i < table; i++) {
    for (let j = 0; j < timestep; j++) {
      worksheet.getCell(`B${14 + (offset * i) + j}`).value = times[j] || '';
    }
  }

  fillVehicleData(worksheet, datasets, timestep, offset);
}

const fillVehicleMonth = (worksheet, datasets, times) => {
  const table = 5;
  const timestep = 31; // Max days in a month
  const offset = 37;

  // Fill time labels in column B
  for (let i = 0; i < table; i++) {
    for (let j = 0; j < timestep; j++) {
      worksheet.getCell(`B${14 + (offset * i) + j}`).value = times[j] || '';
    }
  }

  fillVehicleData(worksheet, datasets, timestep, offset);
}

const fillPredestrianData = (worksheet, datasets, timestep, offset) => {
  let columns = ['C', 'E', 'G', 'I', 'K'];
  if (offset === 31) columns = ['C', 'F', 'I', 'L', 'O'];
  for (let i = 0; i < datasets.length; i++) {
    const dataset = datasets[i];
    const col = columns[i >= 5 ? i - 5 : i];

    // Fill scenario name
    worksheet.getCell(`${col}${10 + (offset * Math.floor(i / 5))}`).value = dataset.name;

    // Fill step data
    for (let step = 0; step < timestep; step++) {
      worksheet.getCell(`${col}${14 + (offset * Math.floor(i / 5)) + step}`).value = dataset.data[step];
    }
  }
}

const fillPedestrianTenMinutes = (worksheet, datasets, times) => {
  const timestep = 6;
  const table = 2;
  const offset = 12;

  // Fill time labels in column B
  for (let i = 0; i < table; i++) {
    for (let j = 0; j < timestep; j++) {
      worksheet.getCell(`B${14 + (offset * i) + j}`).value = times[j] || '';
    }
  }

  fillPredestrianData(worksheet, datasets, timestep, offset);
}

const fillPedestrianWeek = (worksheet, datasets, times) => {
  const timestep = 7;
  const table = 2;
  const offset = 13;

  // Fill time labels in column B
  for (let i = 0; i < table; i++) {
    for (let j = 0; j < timestep; j++) {
      worksheet.getCell(`B${14 + (offset * i) + j}`).value = times[j] || '';
    }
  }

  fillPredestrianData(worksheet, datasets, timestep, offset);
}

const fillPedestrianMonth = (worksheet, datasets, times) => {
  const timestep = 31;
  const table = 2;
  const offset = 37;
  
  // Fill time labels in column B
  for (let i = 0; i < table; i++) {
    for (let j = 0; j < timestep; j++) {
      worksheet.getCell(`B${14 + (offset * i) + j}`).value = times[j] || '';
    }
  }

  fillPredestrianData(worksheet, datasets, timestep, offset);
}

const fillWorksheet = (type, unit, worksheet, datasets) => {
  const data = datasets.datasets || [];
  const times = datasets.labels || [];

  if (type === 'vehicle') {
    switch (unit) {
      case TIME_FRAME.TEN_MINUTES:
        fillVehicleTenMinutes(worksheet, data, times);
        break;
      case TIME_FRAME.DAY: {
        const timestep = 24;
        const offset = 31;
        fillVehicleData(worksheet, data, timestep, offset);
        break;
      }
      case TIME_FRAME.WEEK:
        fillVehicleWeek(worksheet, data, times);
        break;
      case TIME_FRAME.MONTH:
        fillVehicleMonth(worksheet, data, times);
        break;
      case TIME_FRAME.YEAR: {
        const timestep = 12;
        const offset = 18;
        fillVehicleData(worksheet, data, timestep, offset);
        break;
      }
      default:
        console.warn('Unknown time unit, skipping data fill');
    }
  }
  if (type === 'pedestrian') {
    switch (unit) {
      case TIME_FRAME.TEN_MINUTES:
        fillPedestrianTenMinutes(worksheet, data, times);
        break;
      case TIME_FRAME.DAY: {
        const timestep = 24;
        const offset = 31;
        fillPredestrianData(worksheet, data, timestep, offset);
        break;
      }
      case TIME_FRAME.WEEK:
        fillPedestrianWeek(worksheet, data, times);
        break;
      case TIME_FRAME.MONTH:
        fillPedestrianMonth(worksheet, data, times);
        break;
      case TIME_FRAME.YEAR:{
        const timestep = 12;
        const offset = 18;
        fillPredestrianData(worksheet, data, timestep, offset);
        break;
      }
      default:
        console.warn('Unknown time unit, skipping data fill');
    }
  }
}

// Define page break rows for each type and time unit based on the template layout
const TEMPLATE_PAGE_BREAK_ROWS = {
  vehicle: {
    [TIME_FRAME.TEN_MINUTES]: [57],
    [TIME_FRAME.DAY]:          [40, 71, 102, 133], // offset 31
    [TIME_FRAME.WEEK]:         [61],
    [TIME_FRAME.MONTH]:        [46, 83, 120, 157], // offset 37
    [TIME_FRAME.YEAR]:         [45, 81],   // offset 36
  },
  pedestrian: {
    [TIME_FRAME.TEN_MINUTES]: [],
    [TIME_FRAME.DAY]:          [40],
    [TIME_FRAME.WEEK]:         [],
    [TIME_FRAME.MONTH]:        [46],
    [TIME_FRAME.YEAR]:         [],
  },
};

// Retrieve page break rows from template based on type and unit, return in format expected by ExcelJS
// max is set to 16383 to indicate last column, so page break applies to entire row
// man is set to 1 to indicate a manual page break
const getTemplatePageBreaks = (type, unit) => {
  const rows = TEMPLATE_PAGE_BREAK_ROWS[type]?.[unit] || [];
  return rows.map(id => ({ id, max: 16383, man: 1 }));
};

const downloadExcelWithSensorData = async (type, unit, imgData, excelData, fileName) => {
  try {
    const arrayBuffer = await downloadExcelFileFromS3(type, unit);
    // Check if arrayBuffer is valid
    if (arrayBuffer.byteLength === 0) throw new Error('Downloaded file is empty');

    // Verify it's an Excel file by checking the first few bytes
    const view = new Uint8Array(arrayBuffer);
    const isExcel = (view[0] === 0x50 && view[1] === 0x4B) || // ZIP signature (Excel 2007+)
                    (view[0] === 0xD0 && view[1] === 0xCF); // OLE signature (Excel 97-2003)
    if (!isExcel) throw new Error('Downloaded file is not a valid Excel format');

    // Load the Excel workbook
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);  

    // Get the first worksheet
    const worksheet = workbook.worksheets[0];
    if (!worksheet) {
      throw new Error('No worksheet found in the Excel file');
    }

    if (excelData && typeof excelData === 'object') {
      try {
        const sensorName = excelData.sensorName || '';
        const createAt = excelData.createAt || '';
        const datasets = excelData.datasets || [];

        // Fill sensor name and date
        worksheet.getCell('C4').value = sensorName;
        worksheet.getCell('C6').value = createAt;

        // Fill data into worksheet based on type and unit
        fillWorksheet(type, unit, worksheet, datasets);

      } catch (error) {
        throw new Error(`Failed to fill worksheet data: ${error.message}`);
      }
    }

    // Add image data to Excel if provided (non-blocking)
    try {
      insertImageToWorksheet(workbook, worksheet, imgData, type, unit);
    } catch (imageError) {
      console.warn('Image insertion failed:', imageError.message);
      // Continue without image
    }

    // page breaks from the template are always lost. Restore them from known template layout.
    worksheet.rowBreaks = getTemplatePageBreaks(type, unit);

    // Fit to A4 page size.
    worksheet.pageSetup = {
      ...worksheet.pageSetup,
      paperSize: 9,        // 9 = A4
      orientation: 'portrait',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,      // 0 = as many pages tall as needed
    };

    // Generate and download the file with error handling
    try {
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });

      saveAs(blob, fileName);
    } catch (writeError) {
      throw new Error(`Failed to generate Excel file: ${writeError.message}`);
    }
  } catch (error) {
    throw error;
  }
};

const retrieveDateTimeFromVideoKey = (videoKey) => {
  try {
    if (!videoKey) return '';
    const fileName = videoKey.split('/')[videoKey.split('/').length - 1];
    const timestamp = fileName.split('.')[0];
    
    // Convert timestamp to number and treat as milliseconds
    const timestampMs = parseInt(timestamp, 10);
    if (isNaN(timestampMs)) return '';
    
    return moment(timestampMs).add(1, 'minute').format('YYYY-MM-DD HH:mm');
  } catch (error) {
    console.error('Error retrieving date time from video key:', error);
    return '';
  }
}

const directionMapping = DIRECTION_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const directionDisplay = (direction) => {
  if (!direction) return '';

  if (['INBOUND', 'OUTBOUND'].includes(direction)) {
    return directionMapping[direction];
  }
  return `${directionMapping[direction]}方向`;
};

const retrieveLabelFromNameAndDirection = (targetSensor, cameraNumber) => {
  try {
    const name = targetSensor?.name || '';
    const direction1 = targetSensor?.data?.camera1Direction || '';
    const direction2 = targetSensor?.data?.camera2Direction || '';

    // Return label based on camera number
    if (cameraNumber === 1) {
      return `${name} ${directionDisplay(direction1)}`;
    } else
      return `${name} ${directionDisplay(direction2)}`;
  } catch (error) {
    console.error('Error retrieving label from name and direction:', error);
    return '';
  }
}

const retrieveListScenarioFromSensor = (sensor) => {
  if (!sensor) return [];
  const amountSensor = 10;
  const scenarios = [];
  for (let i=1; i<=amountSensor; i++) {
    if (sensor?.data?.[`trafficVolumeScenario${i}`] &&
      sensor?.data?.[`trafficVolumeLat${i}`] &&
      sensor?.data?.[`trafficVolumeLng${i}`] &&
      sensor?.data?.[`trafficVolumeType${i}`]) {
      scenarios.push({
        numberOrder: i,
        name: sensor?.data?.[`trafficVolumeName${i}`] || '',
        type: sensor?.data?.[`trafficVolumeType${i}`] || 'VEHICLE',
        lat: sensor?.data?.[`trafficVolumeLat${i}`],
        lng: sensor?.data?.[`trafficVolumeLng${i}`],
        rotation: sensor?.data?.[`trafficVolumeRotation${i}`],
        color: sensor?.data?.[`trafficVolumeArrowColor${i}`] || '#FF0000',
      });
    }
  }
  return scenarios;
}

function generateNextTenMinuteLabel(label) {
  if (!label || !label.includes(':')) return label; // Return original label if format is unexpected
  const [hour, minute] = label.split(':').map(num => parseInt(num, 10));
  const nextMinute = minute + 10;
  if (nextMinute === 60) {
    const nextHour = (hour + 1) % 24;
    return `${nextHour}:00`;
  }
  return `${hour}:${nextMinute}`;
}

function generateDatasetForVehicle(sensor, scenarios, data) {
  const datasets = [];
  for (let i of scenarios) {
    datasets.push({
      name: getLabelByScenario(i, sensor),
      data: data.vehicleTypeCounts.map((typeCounts, index) => {
        let {car, truck, bus, bike, otherVehicle} = typeCounts[i-1]
        if (car === 0 && truck === 0 && bus === 0 && bike === 0 && otherVehicle === 0)
          car = data?.counts?.[index]?.[i-1] || 0
        return { car, truck, bike, bus, otherVehicle}
      }),
    });
  }
  return datasets;
}

function generateDatasetForPedestrian(sensor, scenarios, data) {
  const datasets = [];
  for (let i of scenarios) {
    datasets.push({
      name: getLabelByScenario(i, sensor),
      data: data.counts.map((count) => count[Number(i) - 1] ? count[Number(i) - 1] : 0),
    });
  }
  return datasets;
}

function generateDataset(sensor, unit, scenario, listScenario, data) {
  let type = 'vehicle';
  if (scenario === 0 || sensor.data[`trafficVolumeType${scenario}`] === TRAFFIC_VOLUME_TYPE.HUMAN)
    type = 'pedestrian';

  if (type === 'vehicle') {
    let relevantScenarios = [];
    // If scenario is 0, get all vehicle scenarios
    if (scenario === -1) {
      relevantScenarios = listScenario.filter(item =>
        item > 0 && sensor.data[`trafficVolumeType${item}`] === TRAFFIC_VOLUME_TYPE.VEHICLE
      );
    } else relevantScenarios = [scenario];

    // Create datasets for each relevant scenario
    const datasets = generateDatasetForVehicle(sensor, relevantScenarios, data);

    switch (unit) {
      case TIME_FRAME.TEN_MINUTES: {
        const labels = data.labels.map(label => `${label}-${generateNextTenMinuteLabel(label)}`);
        return { datasets, labels };
      }
      case TIME_FRAME.DAY:
      case TIME_FRAME.WEEK:
      case TIME_FRAME.MONTH:
      case TIME_FRAME.YEAR: {
        const labels = data.labels.slice();
        return { datasets, labels };
      }
      default:
        return { datasets: [], labels: [] };
    }
  }
  if (type === 'pedestrian') {
    let relevantScenarios = [];
    // If scenario is 0, get all pedestrian scenarios
    if (scenario === 0) {
      relevantScenarios = listScenario.filter(item =>
        item > 0 && sensor.data[`trafficVolumeType${item}`] === TRAFFIC_VOLUME_TYPE.HUMAN
      );
    } else relevantScenarios = [scenario];

    // Create datasets for each relevant scenario
    const datasets = generateDatasetForPedestrian(sensor, relevantScenarios, data);

    switch (unit) {
      case TIME_FRAME.TEN_MINUTES: {
        const labels = data.labels.map(label => `${label}-${generateNextTenMinuteLabel(label)}`);
        return { datasets, labels };
      }
      case TIME_FRAME.DAY:
      case TIME_FRAME.WEEK:
      case TIME_FRAME.MONTH:
      case TIME_FRAME.YEAR: {
        const labels = data.labels.slice();
        return { datasets, labels };
      }
      default:
        return { datasets: [], labels: [] };
    }
  }
}

async function retrieveImage(sensorId) {
  let imgData = null;
  const maxRetries = 3; // Number of retry attempts
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const imageBlob = await downloadFileFromS3(`counted_camera_imgs/${sensorId}`, 'blob');
      if (imageBlob) {
        imgData = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(imageBlob);
        });
      }
      return imgData;
    } catch (error) {
      if (attempt === maxRetries) {
        // Handle specific error codes 404
        const statusCode = error?.metadata?.httpStatusCode;
        if (statusCode === 404){
          imgData = null;
          console.log(`Image not found (404) after ${attempt} attempts`);
        } else throw new Error(`Attempt ${attempt}/${maxRetries} failed: ${error.message}`);
      };
    }
  }
}

const generateExcelFilename = (sensor, unit, scenario, selectedDate, timeUnitLabels) => {
  const dateStr = moment(selectedDate).tz('Asia/Tokyo').format('YYYYMMDD');
  return `${sensor.name}_${scenario === -1 ? '自動車合計' : scenario === 0 ? '歩行者合計' : sensor.data[`trafficVolumeName${scenario}`]}_${timeUnitLabels[unit]}_${dateStr}.xlsx`;
};

async function generateExcelFile(sensor, unit, scenario, listScenario, data, selectedDate, timeUnitLabels) {
  try {
    const datasets = generateDataset(sensor, unit, scenario, listScenario, data);

    const excelData = {
      sensorName: sensor.name || '',
      createAt: moment(selectedDate).format('YYYY年MM月DD日(ddd)'),
      datasets,
    };

    const filename = generateExcelFilename(sensor, unit, scenario, selectedDate, timeUnitLabels);

    let type = 'vehicle';
    if (scenario === 0)
      type = 'pedestrian';
    else {
      type = sensor.data[`trafficVolumeType${scenario}`] === TRAFFIC_VOLUME_TYPE.HUMAN ? 'pedestrian' : 'vehicle';
    }
    // debugger;
    const imgData = await retrieveImage(sensor.id);

    await downloadExcelWithSensorData(type, unit, imgData, excelData, filename);
  } catch (error) {
    console.error('Error generating Excel file:', error);
    throw error;
  }
}

export { 
  downloadExcelWithSensorData,
  retrieveDateTimeFromVideoKey,
  retrieveLabelFromNameAndDirection,
  retrieveListScenarioFromSensor,
  generateExcelFile,
};
